import { type FC, useEffect, useRef, useState } from 'react';

import { useClickOutside } from 'hooks/useClickOutside.tsx';

import Typography from 'components/Typography';

import ChevronDownIcon from 'assets/chevron_down.svg';

import {
  HelperText,
  DropdownContainer,
  DropdownStyled,
  DropdownOptionsContainer,
  DropdownOption,
  DropdownIcon,
} from './Dropdown.styled.ts';
import type {
  DropdownProps,
  DropdownOption as DropdownOptionType,
} from './Dropdown.types.ts';

const Dropdown: FC<DropdownProps> = ({
  title,
  helperText,
  options,
  onChange,
  value,
  placeholder,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  useClickOutside(ref, () => {
    setOpen(false);
  });

  useEffect(() => {
    const findValue = options.find(option => option.value === value);
    if (findValue) {
      setInternalValue(findValue.label);
    }
  }, [value, options]);

  const handleSelect = (option: DropdownOptionType) => {
    onChange(option.value);
    setOpen(false);
  };

  return (
    <DropdownContainer>
      {title && (
        <Typography type="Body" size="small" color="#595959">
          {title}
        </Typography>
      )}
      <DropdownStyled onClick={() => setOpen(!open)} value={internalValue}>
        {internalValue || placeholder}
        <DropdownIcon src={ChevronDownIcon} />
      </DropdownStyled>
      {helperText && (
        <HelperText>
          <Typography type="Label" size="medium" color="#595959">
            {helperText}
          </Typography>
        </HelperText>
      )}

      <DropdownOptionsContainer open={open} ref={ref}>
        {options.map((option, index) => (
          <DropdownOption
            key={`options-dropdown-${index}`}
            onClick={() => handleSelect(option)}
          >
            <Typography type="Body" size="small" style="normal" color="#5C5C5C">
              {option.label}
            </Typography>
          </DropdownOption>
        ))}
      </DropdownOptionsContainer>
    </DropdownContainer>
  );
};

export default Dropdown;
